import type { PropsWithChildren } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { getRequestScope } from '../ssrRequestScope';

/**
 * Единственный QueryClient приложения. Экспортируется, чтобы код вне
 * React-компонентов (mobx-сторы и т.п.) мог дедуплицировать запросы
 * через queryClient.ensureQueryData(...)
 */
export const queryClient = new QueryClient();

/**
 * ЭТАП 2 — per-request QueryClient для SSR.
 * На клиенте это всегда синглтон. На сервере getRouter() кладёт свежий клиент
 * в контекст ЗАПРОСА (AsyncLocalStorage, см. ssrRequestScope.ts + src/server.ts):
 * иначе кэш (и потенциально данные одного пользователя) утекал бы между
 * запросами, а параллельные запросы перетирали бы друг другу module-level
 * переменную — прогон 27.08 ловил из-за этого пустую шапку, пустые списки и
 * hydration-mismatch. Модульный fallback остаётся на случай, когда контекста
 * нет (клиент, тесты, сервер без кастомного входа).
 */
let currentQueryClient = queryClient;

export const getQueryClient = (): QueryClient => {
  const scoped = getRequestScope()?.queryClient;
  return (scoped as QueryClient | undefined) ?? currentQueryClient;
};

export const setQueryClient = (client: QueryClient): void => {
  const scope = getRequestScope();
  if (scope) {
    scope.queryClient = client;
    return;
  }
  currentQueryClient = client;
};

type QueryProviderPropsT = PropsWithChildren<{}>;

export const QueryProvider = (props: QueryProviderPropsT) => {
  const { children } = props;

  return (
    <QueryClientProvider client={getQueryClient()}>
      {children}
    </QueryClientProvider>
  );
};
